import { DISPOSITIONS } from './types';
import type { DashboardData, MetricState } from './types';

/**
 * The eight identities from spec 17 §7, asserted in the view layer.
 *
 * **If one fails, that is a bug to log, not a rounding difference to absorb.** The
 * page is the front of a reporting surface; a dashboard whose tiles disagree with
 * its own tables teaches people to stop believing all of it, not just the wrong
 * number.
 */

export interface IdentityFailure {
  id: number;
  what: string;
  expected: number;
  got: number;
}

/** The number behind a metric, or `undefined` where there deliberately isn't one. */
function num(m: MetricState): number | undefined {
  if (m.state === 'ok' || m.state === 'too_few') return m.value;
  if (m.state === 'zero') return 0;
  // `not_set_up` and `unknown` have no number by design, so they cannot take part
  // in an identity — a missing figure is not a zero to add up.
  return undefined;
}

export function checkIdentities(d: DashboardData): IdentityFailure[] {
  const fails: IdentityFailure[] = [];
  const add = (id: number, what: string, expected: number | undefined, got: number | undefined) => {
    if (expected === undefined || got === undefined) return;
    if (expected !== got) fails.push({ id, what, expected, got });
  };

  const dispositionTotal = DISPOSITIONS.reduce((n, x) => n + d.dispositions[x.key], 0);
  add(1, 'the seven dispositions sum to Conversations handled', num(d.tiles.conversations), dispositionTotal);

  const byAgent =
    d.tiles.appointmentsBy.receptionist +
    d.tiles.appointmentsBy.sms +
    d.tiles.appointmentsBy.email +
    d.tiles.appointmentsBy.recall +
    d.tiles.appointmentsBy.gapFiller;
  add(2, 'the booking agents sum to Appointments created', num(d.tiles.appointments), byAgent);

  const reasonBooked = d.reasons.reduce((n, r) => n + r.booked, 0);
  add(3, 'the reason rows’ booked counts sum to Appointments created', num(d.tiles.appointments), reasonBooked);

  // The reason table IS the source of production, not a figure set beside it (P339).
  const reasonMoney = d.reasons.reduce((n, r) => n + r.valueCents, 0);
  add(4, 'the reason rows’ money sums to Production booked', num(d.tiles.productionCents), reasonMoney);

  add(
    5,
    'warm transfer + urgent triage sum to Needed a human',
    num(d.tiles.neededAHuman),
    d.tiles.warmTransfers + d.tiles.urgentTriage,
  );

  // The badge is asserted against the rows rather than read from the nav, because
  // the nav is the thing that would silently drift.
  const reviewTotal = d.review.reduce((n, r) => n + r.count, 0);
  add(6, 'the four review rows sum to the Train ELVA badge', TRAIN_ELVA_BADGE, reviewTotal);

  const openGaps = d.schedule.days.reduce((n, day) => n + day.openGaps, 0);
  add(
    7,
    'Gap Filler working + unreachable sums to the schedule’s open gaps',
    openGaps,
    d.schedule.gapsWorked + d.schedule.gapsUnreachable,
  );

  const perDay = d.schedule.days.reduce((n, day) => n + day.booked, 0);
  add(8, 'the schedule’s per-day booked sums to the footer’s booked', perDay, scheduleBooked(d));

  return fails;
}

/**
 * The Train ELVA nav badge. Same source, one number (§5.6, §7 identity 6) — the
 * dashboard's four review rows and the badge are the same 29.
 */
export const TRAIN_ELVA_BADGE = 29;

/** Derived, never written: the footer's `booked` is the sum of the days. */
export function scheduleBooked(d: DashboardData): number {
  return d.schedule.days.reduce((n, day) => n + day.booked, 0);
}

export function scheduleCapacity(d: DashboardData): number {
  return d.schedule.days.reduce((n, day) => n + day.capacity, 0);
}

export function scheduleOpenGaps(d: DashboardData): number {
  return d.schedule.days.reduce((n, day) => n + day.openGaps, 0);
}

/**
 * Log failures once per render rather than throwing. A dashboard that refuses to
 * paint because one tile disagrees is worse than one that paints and complains —
 * but a silent absorb is worse than both, which is why this exists at all.
 */
export function reportIdentities(d: DashboardData): IdentityFailure[] {
  const fails = checkIdentities(d);
  if (fails.length > 0 && typeof console !== 'undefined') {
    for (const f of fails) {
      // §7: a failed identity is a bug to log, not a difference to absorb.
      console.error(
        `[dashboard] identity ${String(f.id)} failed — ${f.what}: expected ${String(f.expected)}, got ${String(f.got)}`,
      );
    }
  }
  return fails;
}
